import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { LocalLink as Link, useLocalNavigate as useNavigate } from '@/lib/locale';
import { ProductCard } from '@/components/ProductCard';
import axios from 'axios';
import { toast } from 'sonner';
import appI18n from '@/i18n';
import { useTranslation } from 'react-i18next';
import { useInitialData } from '@/lib/initialData';
import { categoryName, isBears } from '@/lib/catalog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Shop = ({ onAddToCart }) => {
  const initial = useInitialData();
  const [products, setProducts] = useState(() => initial.products || []);
  const [categories, setCategories] = useState(() => initial.categories || []);
  const [loading, setLoading] = useState(!initial.products);
  const [searchParams, setSearchParams] = useSearchParams();
  const { slug: routeSlug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Path-based slug (/category/:slug) takes precedence; ?category= is legacy
  const activeSlug = routeSlug || searchParams.get('category') || '';

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`).catch(() => ({ data: [] })),
      axios.get(`${API}/categories`).catch(() => ({ data: [] })),
    ])
      .then(([p, c]) => {
        setProducts(p.data || []);
        setCategories(c.data || []);
      })
      .catch(() => toast.error(appI18n.t('shop.loadFailed')))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const list = activeSlug
      ? products.filter((p) => p.category === activeSlug || p.category_slug === activeSlug)
      : products;
    // The Bears line comes first, then featured products by featured_order
    return [...list].sort((a, b) => {
      if (isBears(a) !== isBears(b)) return isBears(a) ? -1 : 1;
      if (a.is_featured && !b.is_featured) return -1;
      if (!a.is_featured && b.is_featured) return 1;
      if (a.is_featured && b.is_featured) {
        return (a.featured_order || 99) - (b.featured_order || 99);
      }
      return 0;
    });
  }, [products, activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  // Title per category, in the current language
  useEffect(() => {
    document.title = activeCategory
      ? t('meta.categoryTitle', { name: categoryName(activeCategory, i18n.language) })
      : t('meta.shopTitle');
  }, [activeCategory, t, i18n.language]);

  const setCategory = (slug) => {
    if (slug) navigate(`/category/${slug}`);
    else navigate('/shop');
  };

  return (
    <div className="min-h-screen pt-6 md:pt-10 pb-20" data-testid="shop-page">
      <div className="container mx-auto px-4 md:px-12">
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-6xl font-heading font-bold text-foreground tracking-tight mb-2 md:mb-4">
            {activeCategory ? categoryName(activeCategory, i18n.language) : t('shop.title')}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">
            {activeCategory ? t('shop.productsCount', { count: filtered.length }) : t('shop.subtitle')}
          </p>
        </div>

        {/* Category filter chips */}
        {categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1.5 md:gap-2 mb-8 md:mb-12" data-testid="shop-category-filter">
            <button
              onClick={() => setCategory('')}
              data-testid="shop-cat-all"
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium border transition-all ${
                !activeSlug
                  ? 'bg-accent text-accent-foreground border-accent'
                  : 'border-border bg-card text-muted-foreground hover:border-accent hover:text-foreground'
              }`}
            >
              {t('shop.all')}
            </button>
            {categories.map((c) => (
              <button
                key={c.category_id}
                data-testid={`shop-cat-${c.slug}`}
                onClick={() => setCategory(c.slug)}
                className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium border transition-all ${
                  activeSlug === c.slug
                    ? 'bg-accent text-accent-foreground border-accent'
                    : 'border-border bg-card text-muted-foreground hover:border-accent hover:text-foreground'
                }`}
              >
                {categoryName(c, i18n.language)}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">{t('shop.loading')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground mb-4">{t('shop.empty')}</p>
            <Link to="/shop" className="text-accent hover:text-accent/80 underline">{t('shop.viewAll')}</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {filtered.map((product) => (
              <ProductCard
                key={product.product_id}
                product={product}
                onAddToCart={onAddToCart}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
